import { useEffect, useState } from 'react';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Business from '@mui/icons-material/Business';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Person from '@mui/icons-material/Person';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import GeneralDialog from '../../../../shared/general-dialog';
import {
  DIALOG_CANCEL_BUTTON_SX,
  fromGooglePermissions,
  orgPermissionSummary,
} from './label-schema';
import usePrincipalName from './use-principal-name';
import SharedEmailAutocomplete from '../../../../shared/shared-email-autocomplete';
import { useStore } from '../../../state/zustand-store';
import { handleErrors } from '../../../../utils/errors';
import { getDriveLabelPermissionsQuery } from '../../../../api/users-api';
import {
  LabelPermissionModel,
  LabelPermissionRole,
  setDriveLabelPermissions,
  setDriveLabelPermissionsForEdit,
} from '../../../../api/drive-labels-api';

type OrgRole = LabelPermissionRole | 'RESTRICTED';

const ORG_ROLE_OPTIONS: {
  value: OrgRole;
  label: string;
  description: string;
}[] = [
  {
    value: 'READER',
    label: 'Can view this label',
    description:
      'User(s) can view and search for this label on content they can view or comment on',
  },
  {
    value: 'APPLIER',
    label: 'Can apply labels and set values',
    description:
      'User(s) can apply, set values, and search for this label on content they can edit',
  },
  {
    value: 'RESTRICTED',
    label: 'Restricted',
    description:
      'Only people or groups added in advanced permissions can use this label',
  },
];

const PRINCIPAL_ROLE_OPTIONS = ORG_ROLE_OPTIONS.filter(
  (o) => o.value !== 'RESTRICTED'
);

/**
 * Roles the Admin console doesn't offer (only the API can set them). They're never offered for new
 * people, but someone who already has one keeps it and sees it in their list, so saving the dialog
 * doesn't quietly change their access. Names follow Google's API descriptions.
 */
const API_ONLY_ROLE_OPTIONS: typeof ORG_ROLE_OPTIONS = [
  {
    value: 'ORGANIZER',
    label: 'Can organize this label',
    description:
      'User(s) can pin this label in shared drives they manage and add people who can apply it',
  },
  {
    value: 'EDITOR',
    label: 'Can edit this label',
    description:
      'User(s) can make any change to this label, including deleting it',
  },
];

/** The options for a role select: the usual ones, plus the current role when it's API-only. */
const withCurrentRole = (options: typeof ORG_ROLE_OPTIONS, current: string) => [
  ...options,
  ...API_ONLY_ROLE_OPTIONS.filter((option) => option.value === current),
];

/** The role's name alone, for the closed select; descriptions only show in the open list. */
const roleName = (value: string) =>
  [...ORG_ROLE_OPTIONS, ...API_ONLY_ROLE_OPTIONS].find(
    (option) => option.value === value
  )?.label ?? value;

/** A role in the open list: its name with the description underneath, like the design. */
function roleMenuContent(option: (typeof ORG_ROLE_OPTIONS)[number]) {
  return (
    <Stack>
      <Typography variant="body2">{option.label}</Typography>
      <Typography variant="caption" color="text.secondary">
        {option.description}
      </Typography>
    </Stack>
  );
}

/** Name over email, as in the design; just the email when the directory has no name for it. */
function PrincipalLabel({ principal }: { principal: LabelPermissionModel }) {
  const name = usePrincipalName(principal.email, principal.principalType);
  return (
    <Stack sx={{ flex: 1, minWidth: 0 }}>
      {name && (
        <Typography variant="body2" noWrap>
          {name}
        </Typography>
      )}
      <Typography
        variant={name ? 'caption' : 'body2'}
        color={name ? 'text.secondary' : undefined}
        noWrap
      >
        {principal.email}
      </Typography>
    </Stack>
  );
}

interface LabelPermissionsDialogProps {
  open: boolean;
  handleClose: () => void;
  /** Empty until the label exists in Google; until then changes are kept as pending permissions. */
  labelId: string;
  /** Permissions to apply when the label is created; edited here while there's no label id. */
  pendingPermissions: LabelPermissionModel[];
  onPendingPermissionsChange: (permissions: LabelPermissionModel[]) => void;
  organizationName: string;
  /** Roles that must explain changes are asked for a change log message before saving. */
  requiresChangeLog: boolean;
  /** Edit Label: save through the Edit Label endpoint, which works on published labels too. */
  forEdit?: boolean;
}

export default function LabelPermissionsDialog({
  open,
  handleClose,
  labelId,
  pendingPermissions,
  onPendingPermissionsChange,
  organizationName,
  requiresChangeLog,
  forEdit = false,
}: LabelPermissionsDialogProps) {
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const permissionsQuery = getDriveLabelPermissionsQuery(labelId);
  const { data: current } = useQuery({
    ...permissionsQuery,
    enabled: open && !!labelId,
    // Same as the preview: saves store Google's response, so the cached list stays current.
    staleTime: Infinity,
  });

  const [orgRole, setOrgRole] = useState<OrgRole>('APPLIER');
  const [principals, setPrincipals] = useState<LabelPermissionModel[]>([]);
  const [changeLogOpen, setChangeLogOpen] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  useEffect(() => {
    if (!open || labelId) return;
    const audience = pendingPermissions.find(
      (p) => p.principalType === 'AUDIENCE'
    );
    setOrgRole(audience?.role ?? 'RESTRICTED');
    setPrincipals(
      pendingPermissions.filter((p) => p.principalType !== 'AUDIENCE')
    );
  }, [open, labelId, pendingPermissions]);

  useEffect(() => {
    if (!open || !labelId || !current) return;
    const permissions = fromGooglePermissions(current);
    const audience = permissions.find((p) => p.principalType === 'AUDIENCE');
    setOrgRole(audience?.role ?? 'RESTRICTED');
    setPrincipals(permissions.filter((p) => p.principalType !== 'AUDIENCE'));
  }, [open, labelId, current]);

  const buildPermissions = (): LabelPermissionModel[] => [
    ...(orgRole === 'RESTRICTED'
      ? []
      : [{ principalType: 'AUDIENCE' as const, email: '', role: orgRole }]),
    ...principals,
  ];

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      (forEdit ? setDriveLabelPermissionsForEdit : setDriveLabelPermissions)(
        labelId,
        buildPermissions(),
        changeLogMessage
      ),
    onSuccess: (saved) => {
      // The save returns Google's permissions after the change; show them right away in the side
      // panel and the next time the dialog opens, without waiting for another read.
      queryClient.setQueryData(permissionsQuery.queryKey, saved);
      setChangeLogOpen(false);
      setChangeLogMessage('');
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Label permissions saved.',
      });
      handleClose();
    },
    onError: (error) =>
      handleErrors({
        error,
        snackbarMessage: 'Failed to save label permissions.',
      }),
  });

  const addPrincipals = (emails: string[]) => {
    setPrincipals((prev) => {
      const existing = new Set(prev.map((p) => p.email.toLowerCase()));
      const added = emails
        .filter((email) => !existing.has(email.toLowerCase()))
        .map((email) => ({
          principalType: 'USER' as const,
          email,
          role: 'APPLIER' as const,
        }));
      return [...prev, ...added];
    });
  };

  const handleSave = () => {
    if (!labelId) {
      // Applied to Google when the label is created (Save Draft or Publish).
      onPendingPermissionsChange(buildPermissions());
      handleClose();
      return;
    }
    if (requiresChangeLog) {
      setChangeLogOpen(true);
    } else {
      mutate();
    }
  };

  return (
    <>
      <GeneralDialog
        open={open}
        handleClose={handleClose}
        title="Label permissions"
        maxWidth="sm"
        dialogContent={
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.light' }}>
                <Business fontSize="small" color="primary" />
              </Avatar>
              <Typography sx={{ fontWeight: 500 }}>
                {organizationName}
              </Typography>
            </Stack>
            <FormControl size="small" fullWidth>
              <InputLabel id="label-org-permission-label">
                Permission
              </InputLabel>
              <Select
                id="label-org-permission"
                labelId="label-org-permission-label"
                label="Permission"
                value={orgRole}
                onChange={(e) => setOrgRole(e.target.value as OrgRole)}
                renderValue={roleName}
              >
                {withCurrentRole(ORG_ROLE_OPTIONS, orgRole).map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {roleMenuContent(option)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary">
              {orgPermissionSummary(orgRole, organizationName)}
            </Typography>

            <Divider />

            <Stack spacing={0.5}>
              <Typography sx={{ fontWeight: 500 }}>
                Advanced permissions
              </Typography>
              <Typography variant="caption" color="text.secondary">
                People or groups added can use this label
              </Typography>
            </Stack>
            <SharedEmailAutocomplete
              // Without freeSolo, MUI clears the typed text on every re-render of this component.
              freeSolo
              sendType="Add people or groups"
              sources={['users', 'groups']}
              showSearchIcon
              multiAutoCompleteValue={[]}
              setMultiAutoCompleteValue={addPrincipals}
            />
            {principals.map((principal) => (
              <Stack
                key={principal.email}
                direction="row"
                spacing={1.5}
                alignItems="center"
              >
                <Avatar sx={{ width: 28, height: 28 }}>
                  <Person fontSize="small" />
                </Avatar>
                <PrincipalLabel principal={principal} />
                <FormControl size="small" sx={{ width: 279, flexShrink: 0 }}>
                  <InputLabel id={`perm-${principal.email}-label`}>
                    Permission
                  </InputLabel>
                  <Select
                    id={`perm-${principal.email}`}
                    labelId={`perm-${principal.email}-label`}
                    label="Permission"
                    value={principal.role}
                    onChange={(e) =>
                      setPrincipals((prev) =>
                        prev.map((p) =>
                          p.email === principal.email
                            ? {
                                ...p,
                                role: e.target.value as LabelPermissionRole,
                              }
                            : p
                        )
                      )
                    }
                    renderValue={roleName}
                  >
                    {withCurrentRole(
                      PRINCIPAL_ROLE_OPTIONS,
                      principal.role
                    ).map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {roleMenuContent(option)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <IconButton
                  aria-label={`Remove ${principal.email}`}
                  onClick={() =>
                    setPrincipals((prev) =>
                      prev.filter((p) => p.email !== principal.email)
                    )
                  }
                >
                  <DeleteOutline />
                </IconButton>
              </Stack>
            ))}
            <Stack direction="row" spacing={0.5} alignItems="center">
              <InfoOutlined color="info" sx={{ fontSize: 16 }} />
              <Typography variant="caption" color="info.main">
                Permission changes do not require publishing the label
              </Typography>
            </Stack>
          </Stack>
        }
        primaryButton={{
          title: 'Save',
          action: handleSave,
          loading: isPending,
          // Until the label's permissions have loaded, saving would replace them with the
          // dialog's starting values.
          disabled: !!labelId && !current,
        }}
        secondaryButton={{
          title: 'Cancel',
          action: handleClose,
          loading: false,
          disabled: isPending,
          // GeneralDialog hides the secondary button unless it's marked shown; the design has Cancel.
          shown: true,
          sx: DIALOG_CANCEL_BUTTON_SX,
        }}
      />
      <ChangeLogDialog
        open={changeLogOpen}
        handleClose={() => setChangeLogOpen(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => mutate()}
        mutateLoading={isPending}
      />
    </>
  );
}
